import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatCurrency } from "@/lib/format";
import {
    Building2, BedDouble, UsersRound, ShieldCheck, Archive, Search, Filter, Plus,
    MoreVertical, Eye, Edit3, KeyRound, Pause, Play, Trash2, RefreshCw,
    CircleAlert, LogIn, ChevronDown,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const STATUS_COLORS = {
    active: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
    trial: { bg: "bg-sky-50", text: "text-sky-700", dot: "bg-sky-500" },
    pending_setup: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
    past_due: { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" },
    suspended: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
    archived: { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
    unknown: { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
};

const SUB_STATUS_COLORS = {
    active: "text-emerald-700 bg-emerald-50",
    trial: "text-sky-700 bg-sky-50",
    paused: "text-amber-700 bg-amber-50",
    past_due: "text-orange-700 bg-orange-50",
    cancelled: "text-red-700 bg-red-50",
};

function StatusPill({ value }) {
    const s = STATUS_COLORS[value] || STATUS_COLORS.unknown;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${s.bg} ${s.text}`} data-testid={`hotel-status-${value}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            {(value || "unknown").replace(/_/g, " ")}
        </span>
    );
}

function SubPill({ value }) {
    if (!value) return <span className="text-[11px] text-muted-foreground">—</span>;
    const cls = SUB_STATUS_COLORS[value] || "text-slate-600 bg-slate-100";
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${cls}`}>{value}</span>;
}

function KPICard({ label, value, icon: Icon, color = "orange" }) {
    return (
        <div className="akm-card p-4">
            <div className="flex items-center justify-between">
                <div className="akm-section-title">{label}</div>
                <Icon size={16} className={color === "orange" ? "text-[#F26322]" : color === "red" ? "text-red-500" : color === "amber" ? "text-amber-500" : "text-emerald-500"} />
            </div>
            <div className="mt-1 text-2xl font-bold tracking-tight tabular-nums text-slate-900">{value}</div>
        </div>
    );
}

export default function SuperHotels() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setImpersonation } = useAuth() || {};
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [subFilter, setSubFilter] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [sort, setSort] = useState("recent");
    const [includeArchived, setIncludeArchived] = useState(false);
    const [suspendFor, setSuspendFor] = useState(null);
    const [deleteFor, setDeleteFor] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    const loadRows = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (statusFilter) params.set("status", statusFilter);
            if (subFilter) params.set("subscription_status", subFilter);
            if (planFilter) params.set("plan_id", planFilter);
            if (countryFilter) params.set("country", countryFilter);
            params.set("sort", sort);
            if (includeArchived) params.set("include_archived", "true");
            const { data } = await api.get(`/superadmin/hotels?${params.toString()}`);
            setRows(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    const loadSummary = () => api.get("/superadmin/hotels/summary").then(({ data }) => setSummary(data));
    const loadPlans = () => api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));

    useEffect(() => { loadSummary(); loadPlans(); }, []);
    useEffect(() => {
        const id = setTimeout(loadRows, 250);
        return () => clearTimeout(id);
    }, [q, statusFilter, subFilter, planFilter, countryFilter, sort, includeArchived]);

    // Distinct countries for filter
    const countries = useMemo(() => Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort(), [rows]);

    const openHotel = (id) => navigate(`/admin/hotels/${id}`);

    const doImpersonate = async (h) => {
        const reason = window.prompt(`Open ${h.name} as its hotel admin. Reason (required for audit):`);
        if (!reason || reason.trim().length < 3) return;
        try {
            // Find the primary hotel admin for this hotel
            const usersResp = await api.get(`/superadmin/hotels/${h.id}/users`);
            const admin = usersResp.data.find((u) => u.role === "hotel_admin");
            if (!admin) { toast.error("No hotel admin found"); return; }
            // Preserve the current super_admin token BEFORE the swap so the
            // ImpersonationBanner "Return to Super Admin" button can restore it.
            const origToken = localStorage.getItem("akm_token");
            const { data } = await api.post(`/superadmin/users/${admin.id}/impersonate?reason=${encodeURIComponent(reason)}`);
            if (data.access_token) {
                if (origToken) localStorage.setItem("akm_imp_orig_token", origToken);
                localStorage.setItem("akm_imp_orig_user", JSON.stringify({ role: "super_admin" }));
                localStorage.setItem("akm_imp_token", data.access_token);
                localStorage.setItem("akm_token", data.access_token);
            }
            toast.success(`Impersonating ${admin.name || admin.email}`);
            navigate("/dashboard");
            setTimeout(() => window.location.reload(), 150);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impersonation failed"); }
    };

    const doAction = async (h, endpoint, body = null, successMsg = "Done") => {
        try {
            if (body) await api.post(`/superadmin/hotels/${h.id}/${endpoint}`, body);
            else await api.post(`/superadmin/hotels/${h.id}/${endpoint}`);
            toast.success(successMsg);
            await Promise.all([loadRows(), loadSummary()]);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <Topbar title={t("super_admin.hotels_title")} subtitle={t("super_admin.hotels_subtitle")} />
            <div className="flex-1 overflow-auto p-6" data-testid="super-hotels">
                {/* Summary cards */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    <KPICard label={t("super_admin.hotels.total")} value={summary?.total ?? "—"} icon={Building2} />
                    <KPICard label={t("super_admin.hotels.active")} value={summary?.active ?? "—"} icon={ShieldCheck} color="emerald" />
                    <KPICard label={t("super_admin.hotels.trial")} value={summary?.trial ?? "—"} icon={ShieldCheck} color="orange" />
                    <KPICard label={t("super_admin.hotels.suspended")} value={summary?.suspended ?? "—"} icon={CircleAlert} color="red" />
                    <KPICard label={t("super_admin.hotels.total_rooms")} value={summary?.total_rooms ?? "—"} icon={BedDouble} />
                </div>

                {/* Filters */}
                <div className="akm-card mt-4 flex flex-wrap items-center gap-2 p-3">
                    <div className="relative min-w-[220px] flex-1">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            data-testid="hotels-search"
                            className="akm-input h-9 w-full pl-9 text-[13px]"
                            placeholder={t("super_admin.hotels.search_placeholder")}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <Select value={statusFilter} onChange={setStatusFilter} testid="hotels-status" placeholder="Status">
                        <option value="">{t("super_admin.hotels.all_statuses")}</option>
                        {["active", "trial", "pending_setup", "past_due", "suspended", "archived"].map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                        ))}
                    </Select>
                    <Select value={subFilter} onChange={setSubFilter} testid="hotels-sub" placeholder="Subscription">
                        <option value="">{t("super_admin.hotels.all_subs")}</option>
                        {["active", "trial", "past_due", "paused", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                    <Select value={planFilter} onChange={setPlanFilter} testid="hotels-plan" placeholder="Plan">
                        <option value="">{t("super_admin.hotels.all_plans")}</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                    <Select value={countryFilter} onChange={setCountryFilter} testid="hotels-country" placeholder="Country">
                        <option value="">{t("super_admin.hotels.all_countries")}</option>
                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                    <Select value={sort} onChange={setSort} testid="hotels-sort" placeholder="Sort">
                        <option value="recent">{t("super_admin.hotels.newest_first")}</option>
                        <option value="name">{t("super_admin.hotels.name_asc")}</option>
                        <option value="rooms">Most rooms</option>
                        <option value="last_activity">Recent activity</option>
                    </Select>
                    <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
                        <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            data-testid="hotels-include-archived"
                            className="rounded border-slate-300 accent-[#F26322]"
                        />
                        {t("super_admin.hotels.show_archived")}
                    </label>
                    <button onClick={loadRows} className="akm-btn-ghost h-9 text-[12px]" data-testid="hotels-refresh">
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                    </button>
                    <button
                        onClick={() => setCreateOpen(true)}
                        data-testid="hotels-create"
                        className="akm-btn-primary h-9 text-[12.5px]"
                    >
                        <Plus size={13} /> {t("super_admin.hotels.create_hotel")}
                    </button>
                </div>

                {/* Table */}
                <div className="akm-card mt-4 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_hotel")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_rooms")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_users")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_plan")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_subscription")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_status")}</th>
                                    <th className="px-3 py-2 text-left font-medium">{t("super_admin.hotels.col_last_activity")}</th>
                                    <th className="px-3 py-2 text-right font-medium">&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((h) => (
                                    <tr key={h.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`hotel-row-${h.id}`}>
                                        <td className="px-3 py-2.5">
                                            <button
                                                onClick={() => openHotel(h.id)}
                                                className="text-left font-semibold text-slate-900 hover:text-[#F26322]"
                                            >
                                                {h.name}
                                            </button>
                                            <div className="text-[11px] text-muted-foreground">
                                                {[h.city, h.country].filter(Boolean).join(", ")} · {h.slug}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 tabular-nums">{h.rooms_count}</td>
                                        <td className="px-3 py-2.5 tabular-nums">{h.users_count}</td>
                                        <td className="px-3 py-2.5">
                                            {h.plan_name ? (
                                                <div>
                                                    <div className="font-medium">{h.plan_name}</div>
                                                    {h.plan_price != null && (
                                                        <div className="text-[10.5px] text-muted-foreground tabular-nums">
                                                            {formatCurrency(h.plan_price, h.plan_currency || "EUR")}/mo
                                                        </div>
                                                    )}
                                                </div>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5"><SubPill value={h.subscription_status} /></td>
                                        <td className="px-3 py-2.5"><StatusPill value={h.status} /></td>
                                        <td className="px-3 py-2.5 text-[11.5px] text-muted-foreground">
                                            {h.last_activity_at ? formatDate(h.last_activity_at) : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <ActionsMenu
                                                hotel={h}
                                                onView={() => openHotel(h.id)}
                                                onImpersonate={() => doImpersonate(h)}
                                                onSuspend={() => setSuspendFor(h)}
                                                onReactivate={() => doAction(h, "reactivate", null, "Hotel reactivated")}
                                                onArchive={() => window.confirm(`Archive "${h.name}"? It can be restored later.`) && doAction(h, "archive", null, "Archived")}
                                                onRestore={() => doAction(h, "restore", null, "Restored")}
                                                onDelete={() => setDeleteFor(h)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                {!loading && rows.length === 0 && (
                                    <tr><td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                                        No hotels match your filters.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {suspendFor && (
                <SuspendDialog
                    hotel={suspendFor}
                    onClose={() => setSuspendFor(null)}
                    onDone={async () => { setSuspendFor(null); await Promise.all([loadRows(), loadSummary()]); }}
                />
            )}
            {deleteFor && (
                <DeleteDialog
                    hotel={deleteFor}
                    onClose={() => setDeleteFor(null)}
                    onDone={async () => { setDeleteFor(null); await Promise.all([loadRows(), loadSummary()]); }}
                />
            )}
            {createOpen && (
                <CreateHotelDialog
                    plans={plans}
                    onClose={() => setCreateOpen(false)}
                    onDone={async (created) => {
                        setCreateOpen(false);
                        await Promise.all([loadRows(), loadSummary()]);
                        if (created?.id) navigate(`/admin/hotels/${created.id}`);
                    }}
                />
            )}
        </>
    );
}

function Select({ value, onChange, children, testid, placeholder }) {
    return (
        <div className="relative">
            <select
                className="akm-input h-9 appearance-none pl-3 pr-8 text-[12.5px]"
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                title={placeholder}
            >
                {children}
            </select>
            <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
    );
}

function ActionsMenu({ hotel, onView, onImpersonate, onSuspend, onReactivate, onArchive, onRestore, onDelete }) {
    const { t } = useTranslation();
    const isSuspended = hotel.status === "suspended";
    const isArchived = hotel.status === "archived";
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid={`hotel-actions-${hotel.id}`}
                    className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                >
                    <MoreVertical size={14} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                side="top"
                sideOffset={6}
                collisionPadding={12}
                className="w-56"
                data-testid={`hotel-menu-${hotel.id}`}
            >
                <MenuAction icon={Eye} onSelect={onView}>{t("super_admin.hotels.view_property")}</MenuAction>
                <MenuAction icon={LogIn} onSelect={onImpersonate}>{t("super_admin.hotels.open_as_admin")}</MenuAction>
                <MenuAction icon={Edit3} onSelect={onView}>{t("super_admin.hotels.edit_property")}</MenuAction>
                <MenuAction icon={BedDouble} onSelect={onView}>{t("super_admin.hotels.manage_rooms")}</MenuAction>
                <MenuAction icon={UsersRound} onSelect={onView}>{t("super_admin.hotels.manage_users")}</MenuAction>
                <MenuAction icon={KeyRound} onSelect={onView}>{t("super_admin.hotels.manage_subscription")}</MenuAction>
                <DropdownMenuSeparator />
                {!isSuspended && !isArchived && (
                    <MenuAction icon={Pause} onSelect={onSuspend} tone="amber">{t("super_admin.hotels.suspend")}</MenuAction>
                )}
                {isSuspended && (
                    <MenuAction icon={Play} onSelect={onReactivate} tone="emerald">{t("super_admin.hotels.reactivate")}</MenuAction>
                )}
                {!isArchived && (
                    <MenuAction icon={Archive} onSelect={onArchive}>{t("super_admin.hotels.archive")}</MenuAction>
                )}
                {isArchived && (
                    <MenuAction icon={Play} onSelect={onRestore} tone="emerald">{t("super_admin.hotels.restore")}</MenuAction>
                )}
                <MenuAction icon={Trash2} onSelect={onDelete} tone="red">{t("super_admin.hotels.delete_permanent")}</MenuAction>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function MenuAction({ icon: Icon, children, onSelect, tone = "slate" }) {
    const toneCls = tone === "red" ? "text-red-600 focus:bg-red-50 focus:text-red-700"
        : tone === "amber" ? "text-amber-700 focus:bg-amber-50 focus:text-amber-800"
            : tone === "emerald" ? "text-emerald-700 focus:bg-emerald-50 focus:text-emerald-800"
                : "text-slate-700 focus:bg-slate-50 focus:text-slate-900";
    return (
        <DropdownMenuItem
            onSelect={(e) => { e.preventDefault?.(); onSelect?.(); }}
            className={`flex items-center gap-2 px-3 py-2 text-[12.5px] font-medium cursor-pointer ${toneCls}`}
        >
            <Icon size={13} className="shrink-0" />
            <span className="truncate">{children}</span>
        </DropdownMenuItem>
    );
}

function SuspendDialog({ hotel, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    return (
        <Dialog onClose={onClose} title={`Suspend ${hotel.name}`} testid="suspend-dialog">
            <p className="text-[13px] text-slate-600">
                This will block all users of this hotel from signing in and pause background jobs.
                Data is preserved — you can reactivate at any time. A reason is required for audit.
            </p>
            <textarea
                className="akm-input mt-3 min-h-[80px]"
                placeholder="e.g. Non-payment — reminded on Jul 12"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="suspend-reason"
            />
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost h-9">Cancel</button>
                <button
                    disabled={busy || reason.trim().length < 3}
                    data-testid="suspend-confirm"
                    onClick={async () => {
                        setBusy(true);
                        try {
                            await api.post(`/superadmin/hotels/${hotel.id}/suspend`, { reason: reason.trim() });
                            toast.success("Hotel suspended");
                            await onDone();
                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        finally { setBusy(false); }
                    }}
                    className="h-9 rounded-xl bg-red-600 px-4 text-[13px] font-bold text-white hover:bg-red-700 disabled:opacity-50"
                >
                    Suspend hotel
                </button>
            </div>
        </Dialog>
    );
}

function DeleteDialog({ hotel, onClose, onDone }) {
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        api.get(`/superadmin/hotels/${hotel.id}/delete-preview`)
            .then(({ data }) => setPreview(data))
            .catch(() => setPreview({ can_delete: false, blockers: ["Failed to compute preview"], counts: {} }));
    }, [hotel.id]);
    const match = confirm.trim() === hotel.name.trim();
    const disabled = busy || !match || !preview?.can_delete;
    return (
        <Dialog onClose={onClose} title="Permanently delete hotel" testid="delete-dialog">
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
                <strong>Danger.</strong> This will permanently soft-delete <strong>{hotel.name}</strong>. Consider archiving instead.
            </div>
            {!preview ? (
                <div className="mt-3 text-[12px] text-slate-500">Loading impact preview…</div>
            ) : (
                <>
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">Impact preview</div>
                        <ul className="space-y-0.5 text-[12.5px] text-slate-700">
                            <li>· {preview.counts.users || 0} user account(s)</li>
                            <li>· {preview.counts.rooms || 0} room(s) across {preview.counts.room_types || 0} categor{(preview.counts.room_types || 0) === 1 ? "y" : "ies"}</li>
                            <li>· {preview.counts.reservations || 0} reservation(s)</li>
                            <li>· {preview.counts.integrations || 0} integration(s)</li>
                            <li>· {preview.counts.invoices || 0} invoice(s)</li>
                        </ul>
                    </div>
                    {preview.blockers?.length > 0 && (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <div className="mb-1 flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider text-amber-800">
                                <CircleAlert size={12} /> Deletion blocked
                            </div>
                            <ul className="ml-3 list-disc space-y-0.5 text-[12.5px] text-amber-800">
                                {preview.blockers.map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                        </div>
                    )}
                </>
            )}
            <p className="mt-3 text-[13px] text-slate-600">Type <strong>{hotel.name}</strong> to confirm:</p>
            <input
                className="akm-input mt-1"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="delete-confirm-name"
                placeholder={hotel.name}
            />
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost h-9">Cancel</button>
                <button
                    disabled={disabled}
                    data-testid="delete-confirm-btn"
                    onClick={async () => {
                        setBusy(true);
                        try {
                            await api.delete(`/superadmin/hotels/${hotel.id}`, { data: { confirm_name: confirm.trim() } });
                            toast.success("Hotel deleted");
                            await onDone();
                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        finally { setBusy(false); }
                    }}
                    className="h-9 rounded-xl bg-red-600 px-4 text-[13px] font-bold text-white hover:bg-red-700 disabled:opacity-50"
                >
                    Delete permanently
                </button>
            </div>
        </Dialog>
    );
}

function Dialog({ children, title, onClose, testid }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} data-testid={testid} className="w-full max-w-[500px] rounded-2xl bg-white p-5 shadow-2xl">
                <h3 className="text-[17px] font-semibold text-foreground">{title}</h3>
                <div className="mt-3">{children}</div>
            </div>
        </div>
    );
}

function CreateHotelDialog({ plans, onClose, onDone }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "",
        slug: "",
        contact_email: "",
        phone: "",
        country: "",
        city: "",
        plan_id: "",
        admin_name: "",
        admin_email: "",
        admin_password: "",
    });

    const bind = (k) => ({
        value: form[k],
        onChange: (e) => {
            const v = e.target.value;
            setForm((f) => {
                const next = { ...f, [k]: v };
                // Auto-suggest slug from name
                if (k === "name" && !f.slug) {
                    next.slug = v.toLowerCase()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-+|-+$/g, "");
                }
                return next;
            });
        },
        "data-testid": `create-hotel-${k}`,
    });

    const required = ["name", "slug", "contact_email", "admin_name", "admin_email", "admin_password"];
    const canSubmit = !busy && required.every((k) => form[k].trim().length > 0);

    const submit = async () => {
        if (!canSubmit) {
            toast.error(t("super_admin.hotels.required_fields"));
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form };
            // Strip empty optional fields — backend expects null/omitted, not ""
            ["phone", "country", "city", "plan_id"].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            const { data } = await api.post("/hotels", payload);
            toast.success(t("super_admin.hotels.created_success"));
            await onDone(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create hotel");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                data-testid="create-hotel-dialog"
                className="max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            >
                <h3 className="text-[17px] font-semibold text-foreground">{t("super_admin.hotels.create_dialog_title")}</h3>
                <p className="mt-1 text-[12.5px] text-muted-foreground">{t("super_admin.hotels.create_dialog_desc")}</p>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label={t("super_admin.hotels.field_name") + " *"}>
                        <input className="akm-input" placeholder="Hotel Adriatik" {...bind("name")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_slug") + " *"} hint={t("super_admin.hotels.field_slug_hint")}>
                        <input className="akm-input" placeholder="hotel-adriatik" {...bind("slug")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_contact_email") + " *"}>
                        <input className="akm-input" type="email" placeholder="info@hotel.com" {...bind("contact_email")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_phone")}>
                        <input className="akm-input" placeholder="+355 …" {...bind("phone")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_country")}>
                        <input className="akm-input" placeholder="AL" {...bind("country")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_city")}>
                        <input className="akm-input" placeholder="Tirana" {...bind("city")} />
                    </Field>
                    <Field label={t("super_admin.hotels.field_plan")} className="sm:col-span-2">
                        <select className="akm-input" {...bind("plan_id")}>
                            <option value="">{t("super_admin.hotels.field_no_plan")}</option>
                            {plans.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}{p.price_monthly != null ? ` — ${formatCurrency(p.price_monthly, p.currency || "EUR")}/mo` : ""}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>

                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">
                        {t("super_admin.hotels.section_admin")}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label={t("super_admin.hotels.field_admin_name") + " *"}>
                            <input className="akm-input" placeholder="Emri Mbiemri" {...bind("admin_name")} />
                        </Field>
                        <Field label={t("super_admin.hotels.field_admin_email") + " *"}>
                            <input className="akm-input" type="email" placeholder="admin@hotel.com" {...bind("admin_email")} />
                        </Field>
                        <Field label={t("super_admin.hotels.field_admin_password") + " *"} className="sm:col-span-2">
                            <input className="akm-input" type="password" placeholder="********" autoComplete="new-password" {...bind("admin_password")} />
                        </Field>
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="akm-btn-ghost h-9" data-testid="create-hotel-cancel">
                        {t("super_admin.hotels.cancel")}
                    </button>
                    <button
                        disabled={!canSubmit}
                        onClick={submit}
                        data-testid="create-hotel-submit"
                        className="akm-btn-primary h-9 disabled:opacity-50"
                    >
                        {busy ? t("super_admin.hotels.creating") : t("super_admin.hotels.create")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 block text-[11.5px] font-medium text-slate-600">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-[10.5px] text-muted-foreground">{hint}</span>}
        </label>
    );
}
